import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useReveal } from '../lib/anim/useReveal';

type EngineStatus = 'live' | 'beta' | 'coming-soon';

type Engine = {
  slug: string;
  name: string;
  oneLiner: string;
  status: EngineStatus;
};

const ENGINES: Engine[] = [
  { slug: 'nexlytics-scorer', name: 'Nexlytics', oneLiner: 'File parser + scorer + RAG-grounded auditor', status: 'live' },
  { slug: 'mockingbird', name: 'Mockingbird', oneLiner: 'Competitor scrape → vision blueprint → brand swap → Meta upload', status: 'beta' },
  { slug: 'pulse-check', name: 'Pulse Check', oneLiner: 'Multi-tenant scorecard + cohort benchmark', status: 'live' },
  { slug: 'glide', name: 'Glide', oneLiner: 'Specialist-domain CV + multi-metric scoring', status: 'beta' },
  { slug: 'mobipet', name: 'Mobipet', oneLiner: 'Mobile workflow + SMS coordination + booking', status: 'beta' },
  { slug: 'ai-cto', name: 'AI-CTO / Clarity Advisor', oneLiner: 'RAG over customer corpus → structured advisory output', status: 'live' },
];

const STATUS_STYLE: Record<EngineStatus, React.CSSProperties> = {
  live: {
    background: 'var(--color-accent)',
    color: '#FFFFFF',
    border: 'none',
    padding: '2px 9px',
    borderRadius: '999px',
    fontSize: '11px',
    fontWeight: 500,
  },
  beta: {
    background: 'rgba(11, 18, 32, 0.06)',
    color: 'var(--color-mute)',
    border: '1px solid var(--color-hairline)',
    padding: '2px 9px',
    borderRadius: '999px',
    fontSize: '11px',
    fontWeight: 500,
  },
  'coming-soon': {
    background: 'transparent',
    color: 'var(--color-mute)',
    border: '1px solid var(--color-hairline)',
    padding: '2px 9px',
    borderRadius: '999px',
    fontSize: '11px',
    fontWeight: 500,
  },
};

const STATUS_LABEL: Record<EngineStatus, string> = {
  live: 'Live',
  beta: 'Beta',
  'coming-soon': 'Coming soon',
};

const PICK_CSS = `
.pick-cursor {
  position: absolute;
  width: 14px; height: 14px;
  border-radius: 50%;
  background: var(--color-accent);
  pointer-events: none;
  z-index: 20;
  transition: transform .8s cubic-bezier(.22,.61,.36,1), top .8s cubic-bezier(.22,.61,.36,1), left .8s cubic-bezier(.22,.61,.36,1);
  box-shadow: 0 0 0 3px rgba(79,70,229,0.2);
}
.pick-recommended-pill {
  position: absolute;
  top: -10px; right: 8px;
  background: var(--color-accent-soft);
  color: var(--color-accent);
  border: 1px solid rgba(79, 70, 229, 0.2);
  border-left: 3px solid var(--color-accent);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.04em;
  padding: 3px 9px 3px 7px;
  border-radius: 4px;
  opacity: 0;
  transform: translateY(-4px);
  transition: opacity .4s ease, transform .4s ease;
  white-space: nowrap;
  pointer-events: none;
}
.pick-recommended-pill.visible {
  opacity: 1;
  transform: translateY(0);
}
.pick-card-highlight {
  transition: border-color .4s ease, box-shadow .4s ease;
}
.pick-card-highlight.highlighted {
  border-color: var(--color-accent) !important;
  box-shadow: 0 0 0 1px var(--color-accent);
}
@media (prefers-reduced-motion: reduce) {
  .pick-cursor { transition: none; }
  .pick-recommended-pill { opacity: 1 !important; transform: none !important; transition: none !important; }
}
`;

let pickCSSInjected = false;
function injectPickCSS() {
  if (pickCSSInjected || typeof document === 'undefined') return;
  const style = document.createElement('style');
  style.textContent = PICK_CSS;
  document.head.appendChild(style);
  pickCSSInjected = true;
}

const wait = (ms: number) => new Promise<void>((res) => setTimeout(res, ms));

export function MockupPick() {
  const sectionHeading = useReveal();
  const bodyReveal = useReveal({ delay: 1 });
  const gridReveal = useReveal({ delay: 2 });

  const stageRef = useRef<HTMLDivElement>(null);
  const cursorRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [recommendedVisible, setRecommendedVisible] = useState(false);
  const [highlightedCard, setHighlightedCard] = useState<number | null>(null);
  const hasPlayed = useRef(false);

  useEffect(() => {
    injectPickCSS();
  }, []);

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;

    const prefersReduced =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReduced) {
      setRecommendedVisible(true);
      setHighlightedCard(0);
      return;
    }

    if (!('IntersectionObserver' in window)) {
      playSequence();
      return;
    }

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasPlayed.current) {
          hasPlayed.current = true;
          io.unobserve(entry.target);
          playSequence();
        }
      },
      { threshold: 0.3 }
    );
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function moveCursorToCard(idx: number) {
    const cursor = cursorRef.current;
    const card = cardRefs.current[idx];
    const stage = stageRef.current;
    if (!cursor || !card || !stage) return;

    const stageRect = stage.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    const left = cardRect.left - stageRect.left + cardRect.width * 0.75;
    const top = cardRect.top - stageRect.top + cardRect.height * 0.25;

    cursor.style.left = `${left}px`;
    cursor.style.top = `${top}px`;
  }

  async function playSequence() {
    const cursor = cursorRef.current;
    const stage = stageRef.current;
    if (!cursor || !stage) return;

    // start off-screen
    cursor.style.left = '-20px';
    cursor.style.top = '-20px';
    cursor.style.opacity = '1';

    await wait(500);

    // visit cards 1, 3, 5 quickly, then land on 0 (Nexlytics)
    for (const idx of [1, 3, 5]) {
      moveCursorToCard(idx);
      setHighlightedCard(idx);
      await wait(700);
      setHighlightedCard(null);
    }

    await wait(200);
    moveCursorToCard(0);
    setHighlightedCard(0);
    await wait(800);
    setRecommendedVisible(true);
  }

  return (
    <section
      className="py-24"
      style={{ background: 'var(--color-bg)', borderTop: '1px solid var(--color-hairline)' }}
    >
      <div className="max-w-6xl mx-auto px-6">
        {/* Section heading */}
        <div
          ref={sectionHeading.ref}
          {...sectionHeading.props}
          className={`mb-4${sectionHeading.props.className ? ' ' + sectionHeading.props.className : ''}`}
        >
          <p className="eyebrow" data-r>
            <span className="eyebrow-dash" />
            <span>02 · PICK</span>
          </p>
          <h2
            className="text-2xl md:text-3xl font-bold max-w-xl"
            style={{ fontFamily: 'var(--font-display)', color: 'var(--color-ink)' }}
          >
            Six engines, each proven on an Onwards product.
          </h2>
        </div>

        <div
          ref={bodyReveal.ref}
          {...bodyReveal.props}
          className={`mb-10${bodyReveal.props.className ? ' ' + bodyReveal.props.className : ''}`}
        >
          <p
            className="text-base max-w-lg"
            style={{ color: 'var(--color-mute)', fontFamily: 'var(--font-body)' }}
          >
            Pick the one that fits your ICP. Configure the wrap. Ship.
          </p>
        </div>

        {/* Mockup grid with cursor */}
        <div
          ref={(el) => {
            (gridReveal.ref as React.MutableRefObject<HTMLDivElement | null>).current = el;
            (stageRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
          }}
          {...gridReveal.props}
          className={`relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5${gridReveal.props.className ? ' ' + gridReveal.props.className : ''}`}
        >
          {/* Cursor dot */}
          <div
            ref={cursorRef}
            className="pick-cursor"
            aria-hidden
            style={{ opacity: 0 }}
          />

          {ENGINES.map((engine, i) => (
            <div
              key={engine.slug}
              ref={(el) => { cardRefs.current[i] = el; }}
              className={`pick-card-highlight flex flex-col gap-4 p-6${highlightedCard === i ? ' highlighted' : ''}`}
              style={{
                background: 'var(--color-surface)',
                border: '1px solid var(--color-hairline)',
                borderRadius: '20px',
                position: 'relative',
              }}
            >
              {i === 0 && (
                <span className={`pick-recommended-pill${recommendedVisible ? ' visible' : ''}`}>
                  Recommended for your intake
                </span>
              )}

              <div className="flex items-start justify-between gap-3">
                <h3
                  className="text-base font-semibold leading-snug"
                  style={{ color: 'var(--color-ink)', fontFamily: 'var(--font-display)' }}
                >
                  {engine.name}
                </h3>
                <span style={{ ...STATUS_STYLE[engine.status], fontFamily: 'var(--font-mono)' }}>
                  {STATUS_LABEL[engine.status]}
                </span>
              </div>

              <p
                className="text-sm leading-relaxed flex-1"
                style={{ color: 'var(--color-mute)', fontFamily: 'var(--font-body)' }}
              >
                {engine.oneLiner}
              </p>

              <Link
                to={`/engines/${engine.slug}`}
                className="text-sm font-medium transition-opacity hover:opacity-70"
                style={{ color: 'var(--color-accent)', fontFamily: 'var(--font-body)' }}
              >
                Learn more →
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
